<template>
  <main class="sales-page">
    <div class="page-top">
      <div>
        <p class="eyebrow">SALES REPORTING</p>
        <h1>Sales Performance</h1>
        <p class="intro">
          Follow sales through to the money received, with the source
          transactions behind each figure.
        </p>
      </div>
      <router-link class="previous-link" to="/admin/order-analytics"
        >Previous view <span aria-hidden="true">↗</span></router-link
      >
    </div>

    <div v-if="capabilityState === 'loading'" class="state-panel" role="status">
      Checking access to sales reports…
    </div>
    <div
      v-else-if="capabilityState === 'error'"
      class="state-panel state-error"
      role="alert"
    >
      <h2>We could not check report access</h2>
      <p>Please try again.</p>
      <button type="button" class="secondary-button" @click="loadCapabilities">
        Retry
      </button>
    </div>
    <div v-else-if="capabilityState === 'unavailable'" class="state-panel">
      <h2>Sales Performance is not available</h2>
      <p>This view has not been enabled for your account.</p>
    </div>
    <template v-else>
      <div class="mode-switch" role="group" aria-label="Report view">
        <button
          type="button"
          :class="{ active: draft.mode === 'ceremony' }"
          :aria-pressed="draft.mode === 'ceremony'"
          @click="setMode('ceremony')"
        >
          <span class="mode-title">Ceremony performance</span
          ><span class="mode-description">Cumulative through a date</span>
        </button>
        <button
          type="button"
          :class="{ active: draft.mode === 'activity' }"
          :aria-pressed="draft.mode === 'activity'"
          @click="setMode('activity')"
        >
          <span class="mode-title">Transaction activity</span
          ><span class="mode-description">Movements in a period</span>
        </button>
      </div>

      <form
        class="filter-panel"
        :class="{ collapsed: !filtersExpanded }"
        @submit.prevent="applyFilters"
      >
        <div class="filter-heading">
          <div>
            <h2>Report scope</h2>
            <p>
              {{
                draft.mode === "ceremony"
                  ? "Includes advance purchases and later movements through the cutoff."
                  : "Payments and adjustments appear on the date they occurred."
              }}
            </p>
          </div>
          <button
            type="button"
            class="secondary-button"
            :aria-expanded="filtersExpanded"
            @click="filtersExpanded = !filtersExpanded"
          >
            {{ filtersExpanded ? "Hide filters" : "Edit filters" }}</button
          ><span class="timezone">All dates UTC</span>
        </div>
        <div class="filter-grid">
          <label v-if="tenantOptions.length > 1"
            ><span>Organisation</span
            ><select v-model="draft.tenant" @change="onTenantChange">
              <option value="">All permitted organisations</option>
              <option
                v-for="tenant in tenantOptions"
                :key="tenant"
                :value="tenant"
              >
                {{ tenantLabel(tenant) }}
              </option>
            </select></label
          >
          <label
            ><span>University</span
            ><select
              v-model="draft.university_slug"
              @change="onUniversityChange"
            >
              <option value="">All permitted universities</option>
              <option
                v-for="university in universityOptions"
                :key="`${university.tenant}-${university.slug}`"
                :value="university.slug"
              >
                {{ university.name }}
              </option>
            </select></label
          >
          <label
            ><span>Channel</span
            ><select v-model="draft.channel">
              <option value="all">Online and in person</option>
              <option value="online">Online</option>
              <option value="pos">In person</option>
            </select></label
          >
          <label v-if="draft.mode === 'ceremony'"
            ><span>Sales through</span
            ><input
              v-model="draft.cutoff"
              type="date"
              required
              aria-label="Sales through date UTC"
          /></label>
          <template v-else
            ><label
              ><span>From</span
              ><input
                v-model="draft.from"
                type="date"
                required
                aria-label="From date UTC" /></label
            ><label
              ><span>To</span
              ><input
                v-model="draft.to"
                type="date"
                required
                aria-label="To date UTC" /></label
          ></template>
        </div>
        <details v-if="ceremonyOptions.length" class="ceremony-picker">
          <summary>
            Ceremonies
            <span>{{
              draft.ceremony_ids.length
                ? `${draft.ceremony_ids.length} selected`
                : "All in scope"
            }}</span>
          </summary>
          <div class="ceremony-list">
            <label v-for="ceremony in ceremonyOptions" :key="ceremony.id"
              ><input
                v-model="draft.ceremony_ids"
                type="checkbox"
                :value="ceremony.id"
              /><span>{{ ceremony.name }}</span></label
            >
          </div>
        </details>
        <label v-if="capabilities.disputes_enabled" class="dispute-toggle"
          ><input v-model="draft.include_disputes" type="checkbox" /><span
            ><strong>Include dispute movements</strong
            ><small
              >Includes dispute deductions, fees and reversals in totals and
              transaction detail.</small
            ></span
          ></label
        >
        <div class="filter-actions">
          <button
            type="submit"
            class="primary-button"
            :disabled="invalidDates || reportState === 'loading'"
          >
            Apply filters</button
          ><button type="button" class="text-button" @click="resetFilters">
            Reset filters</button
          ><span v-if="invalidDates" class="validation" role="alert"
            >The end date must be on or after the start date.</span
          >
        </div>
      </form>

      <div class="applied-bar">
        <span class="applied-label">Showing</span
        ><strong>{{ appliedDescription }}</strong
        ><span v-if="hasPendingChanges" class="pending-note"
          >Unapplied changes</span
        ><button
          type="button"
          class="export-button"
          :disabled="
            !report || reportState !== 'ready' || hasPendingChanges || exporting
          "
          @click="exportCsv"
        >
          {{ exporting ? "Preparing CSV…" : "Export CSV" }}
        </button>
        <span v-if="exportError" class="validation" role="alert">{{ exportError }}</span>
      </div>

      <div v-if="reportState === 'loading'" class="state-panel" role="status">
        Loading sales figures…
      </div>
      <div
        v-else-if="reportState === 'error'"
        class="state-panel state-error"
        role="alert"
      >
        <h2>Sales figures could not be loaded</h2>
        <p>{{ reportError }}</p>
        <button type="button" class="secondary-button" @click="fetchReport">
          Retry
        </button>
      </div>
      <template v-else-if="report">
        <div
          v-if="report.coverage && report.coverage.status === 'not_synced'"
          class="notice notice-warning"
          role="status"
        >
          <strong>Sales data is not ready for this scope.</strong
          ><span
            >There is no completed sync covering this report yet. Figures will
            appear after source recovery completes.</span
          >
        </div>
        <div
          v-else-if="report.coverage && report.coverage.status === 'incomplete'"
          class="notice notice-warning"
          role="status"
        >
          <strong>Some financial evidence is missing.</strong
          ><span
            >Known amounts are shown, but totals may change as data
            arrives.</span
          >
        </div>
        <div
          v-if="report.freshness && report.freshness.stale"
          class="notice notice-warning"
          role="status"
        >
          <strong>Data refresh is delayed.</strong
          ><span
            >Evidence through:
            {{ formatDateTime(report.freshness.last_successful_sync) }}.</span
          >
        </div>
        <div
          v-else-if="report.freshness && report.freshness.last_successful_sync"
          class="freshness"
        >
          Evidence through
          {{ formatDateTime(report.freshness.last_successful_sync) }} · Report
          generated {{ formatDateTime(report.generated_at) }}
        </div>
        <div v-if="coverageDetails.length" class="coverage-detail">
          <strong>Data notes</strong>
          <ul>
            <li v-for="(note, index) in coverageDetails" :key="index">
              {{ note }}
            </li>
          </ul>
        </div>

        <section
          v-if="!report.coverage || report.coverage.status !== 'not_synced'"
          class="overview-section"
          aria-labelledby="overview-title"
        >
          <div class="section-heading">
            <div>
              <p class="eyebrow">AT A GLANCE</p>
              <h2 id="overview-title">
                {{
                  applied.mode === "ceremony"
                    ? "Ceremony performance"
                    : "Transaction activity"
                }}
              </h2>
            </div>
            <span class="basis-pill">{{
              applied.include_disputes
                ? "Including disputes"
                : "Excluding disputes"
            }}</span>
          </div>
          <div class="count-grid">
            <div class="count-card">
              <span>Orders</span
              ><strong>{{
                formatCount(report.counts && report.counts.orders)
              }}</strong
              ><small>Distinct paid purchases</small>
            </div>
            <div class="count-card">
              <span>Clips</span
              ><strong>{{
                formatCount(report.counts && report.counts.clip_quantity)
              }}</strong
              ><small>Product quantity</small>
            </div>
            <div class="count-card">
              <span>Digital downloads</span
              ><strong>{{
                formatCount(
                  report.counts && report.counts.digital_download_quantity
                )
              }}</strong
              ><small>Product quantity</small>
            </div>
          </div>
          <div v-if="trendRows.length" class="trend-card">
            <div>
              <h3>Orders over time</h3>
              <p>
                Paid purchases by
                {{ report.trend.granularity === "month" ? "month" : "day" }} ·
                counts, across all currencies
              </p>
            </div>
            <div class="trend-bars" role="img" :aria-label="trendDescription">
              <div
                v-for="row in trendRows"
                :key="row.date"
                class="trend-column"
              >
                <div
                  class="trend-bar"
                  :style="{
                    height: `${Math.round((row.orders / trendMax) * 100)}%`,
                  }"
                  :title="`${row.date}: ${row.orders} orders`"
                ></div>
              </div>
            </div>
            <div class="trend-axis">
              <span>{{ trendRows[0].date }}</span
              ><span>{{ trendRows[trendRows.length - 1].date }}</span>
            </div>
            <details class="trend-data">
              <summary>View order counts</summary>
              <table>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in trendRows" :key="row.date">
                    <td>{{ row.date }}</td>
                    <td>{{ row.orders }}</td>
                  </tr>
                </tbody>
              </table>
            </details>
          </div>
          <div v-if="noMovements" class="empty-inline">
            No financial movements match these filters. Try a wider date or
            ceremony scope.
          </div>
          <div class="money-grid">
            <div class="money-panel">
              <div class="money-heading">
                <h3>Customer payments</h3>
                <p>Original payment currency · actual amounts</p>
              </div>
              <div
                v-for="money in report.charge_currencies || []"
                :key="money.currency"
                class="currency-block"
              >
                <div class="currency-title">{{ money.currency }}</div>
                <dl>
                  <div>
                    <dt>Sales paid</dt>
                    <dd>{{ formatMoney(money.sales_minor, money) }}</dd>
                  </div>
                  <div>
                    <dt>Successful refunds</dt>
                    <dd>{{ negativeMagnitude(money.refunds_minor, money) }}</dd>
                  </div>
                </dl>
              </div>
              <p v-if="!(report.charge_currencies || []).length" class="muted">
                No customer payment amounts available.
              </p>
            </div>
            <div class="money-panel">
              <div class="money-heading">
                <h3>Processor settlement</h3>
                <p>Settlement currency · after processor movements</p>
              </div>
              <div
                v-for="money in report.settlement_currencies || []"
                :key="money.currency"
                class="currency-block"
              >
                <div class="currency-title">
                  {{ money.currency }}
                  <span v-if="money.complete === false" class="incomplete-tag"
                    >Incomplete</span
                  >
                </div>
                <dl>
                  <div>
                    <dt>Gross payments</dt>
                    <dd>{{ formatMoney(money.payments_minor, money) }}</dd>
                  </div>
                  <div>
                    <dt>Refunds</dt>
                    <dd>{{ negativeMagnitude(money.refunds_minor, money) }}</dd>
                  </div>
                  <div>
                    <dt>Processing fees</dt>
                    <dd>
                      {{
                        negativeMagnitude(money.processing_fees_minor, money)
                      }}
                    </dd>
                  </div>
                  <template v-if="applied.include_disputes"
                    ><div>
                      <dt>Dispute deductions</dt>
                      <dd>
                        {{
                          negativeMagnitude(
                            money.dispute_deductions_minor,
                            money
                          )
                        }}
                      </dd>
                    </div>
                    <div>
                      <dt>Dispute fees</dt>
                      <dd>{{ signedFee(money.dispute_fees_minor, money) }}</dd>
                    </div>
                    <div>
                      <dt>Dispute reversals</dt>
                      <dd>
                        +{{ formatMoney(money.dispute_reversals_minor, money) }}
                      </dd>
                    </div></template
                  >
                  <div class="net-row">
                    <dt>
                      {{
                        money.net_label ||
                        (applied.include_disputes
                          ? "Processor net proceeds"
                          : "Net before dispute adjustments")
                      }}
                    </dt>
                    <dd>{{ formatMoney(money.net_minor, money) }}</dd>
                  </div>
                </dl>
              </div>
              <p
                v-if="!(report.settlement_currencies || []).length"
                class="muted"
              >
                No settlement amounts available.
              </p>
            </div>
          </div>
        </section>

        <section
          v-if="!report.coverage || report.coverage.status !== 'not_synced'"
          class="details-section"
          aria-labelledby="breakdown-title"
        >
          <div class="section-heading">
            <div>
              <p class="eyebrow">EXPLORE</p>
              <h2 id="breakdown-title">Where sales came from</h2>
            </div>
          </div>
          <div class="detail-grid">
            <div class="detail-card">
              <h3>By ceremony</h3>
              <p v-if="!(report.by_ceremony || []).length" class="muted">
                No ceremonies in this report.
              </p>
              <details
                v-for="row in report.by_ceremony || []"
                :key="`${row.tenant}-${row.ceremony_id}`"
                class="breakdown"
              >
                <summary>
                  <span
                    ><strong>{{
                      row.university_name || row.university_slug
                    }}</strong
                    ><small>{{
                      formatDateTime(row.ceremony_datetime)
                    }}</small></span
                  ><span class="breakdown-headline"
                    >{{ orderCountLabel(row.counts && row.counts.orders) }}
                    <small>{{ netSummary(row) }}</small></span
                  >
                </summary>
                <div class="breakdown-money">
                  <div
                    v-for="money in row.charge_currencies || []"
                    :key="`charge-${money.currency}`"
                  >
                    <strong>{{ money.currency }} customer payments</strong
                    ><span
                      >Sales {{ formatMoney(money.sales_minor, money) }} ·
                      Refunds
                      {{ formatMoney(money.refunds_minor, money) }}</span
                    >
                  </div>
                  <div
                    v-for="money in row.settlement_currencies || []"
                    :key="`settlement-${money.currency}`"
                  >
                    <strong>{{ money.currency }} settlement</strong
                    ><span
                      >{{ money.net_label || "Processor net proceeds" }}
                      {{ formatMoney(money.net_minor, money) }} · Fees
                      {{
                        formatMoney(money.processing_fees_minor, money)
                      }}</span
                    >
                  </div>
                  <button
                    type="button"
                    class="text-button drill-button"
                    @click="selectCeremony(row)"
                  >
                    Show this ceremony
                  </button>
                </div>
              </details>
            </div>
            <div class="detail-card">
              <h3>By channel</h3>
              <p v-if="!(report.by_channel || []).length" class="muted">
                No channel activity in this report.
              </p>
              <details
                v-for="row in report.by_channel || []"
                :key="row.channel"
                class="breakdown"
              >
                <summary>
                  <strong>{{ channelLabel(row.channel) }}</strong
                  ><span class="breakdown-headline"
                    >{{ orderCountLabel(row.counts && row.counts.orders) }}
                    <small>{{ netSummary(row) }}</small></span
                  >
                </summary>
                <div class="breakdown-money">
                  <div
                    v-for="money in row.charge_currencies || []"
                    :key="`charge-${money.currency}`"
                  >
                    <strong>{{ money.currency }} customer payments</strong
                    ><span
                      >Sales {{ formatMoney(money.sales_minor, money) }} ·
                      Refunds
                      {{ formatMoney(money.refunds_minor, money) }}</span
                    >
                  </div>
                  <div
                    v-for="money in row.settlement_currencies || []"
                    :key="`settlement-${money.currency}`"
                  >
                    <strong>{{ money.currency }} settlement</strong
                    ><span
                      >{{ money.net_label || "Processor net proceeds" }}
                      {{ formatMoney(money.net_minor, money) }} · Fees
                      {{
                        formatMoney(money.processing_fees_minor, money)
                      }}</span
                    >
                  </div>
                </div>
              </details>
              <h3 class="subheading">Products sold</h3>
              <div
                v-for="product in report.products || []"
                :key="product.product_type"
                class="product-row"
              >
                <div>
                  <strong>{{ productLabel(product.product_type) }}</strong
                  ><small
                    >{{ formatCount(product.quantity) }} sold<span
                      v-if="product.unknown_amount_count"
                    >
                      · {{ product.unknown_amount_count }} amount unknown</span
                    ></small
                  >
                </div>
                <div class="product-money">
                  <span
                    v-for="amount in product.currencies || []"
                    :key="amount.currency"
                    >{{ formatMoney(amount.sales_minor, amount) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          v-if="!report.coverage || report.coverage.status !== 'not_synced'"
          class="transactions-section"
          aria-labelledby="transactions-title"
        >
          <div class="section-heading">
            <div>
              <p class="eyebrow">AUDIT TRAIL</p>
              <h2 id="transactions-title">Financial transactions</h2>
              <p>
                Payments, refunds, fees and adjustments in the applied scope.
              </p>
            </div>
            <span class="transaction-count"
              >{{
                formatCount(report.transactions && report.transactions.total)
              }}
              movements</span
            >
          </div>
          <div
            v-if="
              !(
                report.transactions &&
                report.transactions.rows &&
                report.transactions.rows.length
              )
            "
            class="empty-inline"
          >
            No transactions to show for this page.
          </div>
          <div v-else class="transaction-list">
            <details
              v-for="movement in report.transactions.rows"
              :key="movement.id"
              class="transaction"
            >
              <summary>
                <span class="transaction-main"
                  ><strong>{{ movementLabel(movement.movement_type) }}</strong
                  ><small
                    >{{ formatDateTime(movement.occurred_at) }} ·
                    {{ channelLabel(movement.channel) }}</small
                  ></span
                ><span class="transaction-amount"
                  >{{ movementAmount(movement)
                  }}<small>{{ movementAmountBasis(movement) }}</small
                  ><small>{{
                    evidenceLabel(movement.evidence_state)
                  }}</small></span
                ><span class="chevron" aria-hidden="true">⌄</span>
              </summary>
              <div class="transaction-detail">
                <dl>
                  <div>
                    <dt>Original amount</dt>
                    <dd>
                      {{
                        nullableMoney(
                          movement.charge_amount_minor,
                          movement.charge_currency,
                          movement.charge_currency_exponent
                        )
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>Settlement amount</dt>
                    <dd>
                      {{
                        nullableMoney(
                          movement.settlement_amount_minor,
                          movement.settlement_currency,
                          movement.settlement_currency_exponent
                        )
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt>University</dt>
                    <dd>{{ universityName(movement.university_id) }}</dd>
                  </div>
                  <div>
                    <dt>Ceremony</dt>
                    <dd>{{ ceremonyName(movement.ceremony_id) }}</dd>
                  </div>
                  <div>
                    <dt>Order reference</dt>
                    <dd>{{ movement.order_id || "—" }}</dd>
                  </div>
                  <div>
                    <dt>Source reference</dt>
                    <dd>
                      {{ movement.source_type }} · {{ movement.source_id }}
                    </dd>
                  </div>
                  <div>
                    <dt>Processor account</dt>
                    <dd>{{ movement.processor_account_id || "—" }}</dd>
                  </div>
                </dl>
                <div
                  v-if="movement.items && movement.items.length"
                  class="transaction-items"
                >
                  <strong>Products on purchase</strong>
                  <p v-for="(item, index) in movement.items" :key="index">
                    {{ item.quantity }} ×
                    {{ productLabel(item.product_type) }} ·
                    {{
                      nullableMoney(
                        item.paid_amount_minor,
                        item.currency,
                        item.currency_exponent
                      )
                    }}
                  </p>
                </div>
              </div>
            </details>
          </div>
          <div
            v-if="
              report.transactions &&
              report.transactions.total > report.transactions.limit
            "
            class="pagination"
          >
            <button
              type="button"
              class="secondary-button"
              :disabled="applied.offset === 0 || reportState === 'loading'"
              @click="changePage(-1)"
            >
              Previous</button
            ><span
              >{{ applied.offset + 1 }}–{{
                Math.min(
                  applied.offset + report.transactions.limit,
                  report.transactions.total
                )
              }}
              of {{ report.transactions.total }}</span
            ><button
              type="button"
              class="secondary-button"
              :disabled="
                applied.offset + report.transactions.limit >=
                  report.transactions.total || reportState === 'loading'
              "
              @click="changePage(1)"
            >
              Next
            </button>
          </div>
        </section>
      </template>
    </template>
  </main>
</template>

<script>
import axios from "axios";

const todayUtc = () => new Date().toISOString().slice(0, 10);
const initialFilters = () => ({
  tenant: "",
  university_slug: "",
  ceremony_ids: [],
  mode: "ceremony",
  cutoff: todayUtc(),
  from: "",
  to: todayUtc(),
  channel: "all",
  include_disputes: true,
  offset: 0,
});

export default {
  name: "SalesPerformance",
  data() {
    return {
      capabilityState: "loading",
      capabilities: {},
      draft: initialFilters(),
      applied: initialFilters(),
      report: null,
      reportState: "idle",
      reportError: "",
      exporting: false,
      exportError: "",
      reportSerial: 0,
      filtersExpanded: false,
    };
  },
  computed: {
    tenantOptions() {
      return (this.capabilities.scopes || []).map((scope) => scope.tenant);
    },
    universityOptions() {
      return (this.capabilities.scopes || [])
        .filter(
          (scope) => !this.draft.tenant || scope.tenant === this.draft.tenant
        )
        .reduce(
          (all, scope) =>
            all.concat(
              (scope.universities || []).map((university) => ({
                ...university,
                tenant: scope.tenant,
              }))
            ),
          []
        );
    },
    ceremonyOptions() {
      return this.universityOptions
        .filter(
          (university) =>
            !this.draft.university_slug ||
            university.slug === this.draft.university_slug
        )
        .reduce(
          (all, university) =>
            all.concat(
              (university.ceremonies || []).map((ceremony) => ({
                ...ceremony,
                name: `${university.name} · ${this.formatDateTime(
                  ceremony.datetime
                )}`,
              }))
            ),
          []
        );
    },
    invalidDates() {
      return (
        this.draft.mode === "activity" &&
        !!this.draft.from &&
        !!this.draft.to &&
        this.draft.from > this.draft.to
      );
    },
    hasPendingChanges() {
      const draft = {
        ...this.draft,
        offset: 0,
        ceremony_ids: [...this.draft.ceremony_ids].sort(),
      };
      const applied = {
        ...this.applied,
        offset: 0,
        ceremony_ids: [...this.applied.ceremony_ids].sort(),
      };
      return JSON.stringify(draft) !== JSON.stringify(applied);
    },
    appliedDescription() {
      const parts = [
        this.applied.mode === "ceremony"
          ? `Cumulative through ${this.applied.cutoff}`
          : `${this.applied.from} to ${this.applied.to}`,
        "UTC",
      ];
      if (this.applied.tenant)
        parts.push(this.tenantLabel(this.applied.tenant));
      parts.push(this.applied.university_slug || "All permitted universities");
      if (this.applied.ceremony_ids.length)
        parts.push(`${this.applied.ceremony_ids.length} ceremonies`);
      if (this.applied.channel !== "all")
        parts.push(this.channelLabel(this.applied.channel));
      parts.push(
        this.applied.include_disputes
          ? "including disputes"
          : "excluding disputes"
      );
      return parts.join(" · ");
    },
    coverageDetails() {
      const coverage = this.report && this.report.coverage;
      if (!coverage) return [];
      const notes = [...(coverage.messages || [])];
      if (coverage.missing_fees)
        notes.push(`${coverage.missing_fees} fee amounts pending`);
      if (coverage.unknown_line_amounts)
        notes.push(
          `${coverage.unknown_line_amounts} product sale amounts unknown`
        );
      if (coverage.unmapped_count)
        notes.push(`${coverage.unmapped_count} source movements need review`);
      return notes;
    },
    noMovements() {
      return (
        !!this.report &&
        !!this.report.transactions &&
        this.report.transactions.total === 0 &&
        this.report.coverage &&
        this.report.coverage.status === "complete"
      );
    },
    trendRows() {
      return this.report &&
        this.report.trend &&
        Array.isArray(this.report.trend.rows)
        ? this.report.trend.rows
        : [];
    },
    trendMax() {
      return Math.max(1, ...this.trendRows.map((row) => row.orders));
    },
    trendDescription() {
      return `Orders over time, ${this.trendRows.length} ${
        this.report.trend.granularity === "month" ? "months" : "days"
      }, ${this.trendRows.reduce(
        (sum, row) => sum + row.orders,
        0
      )} orders. Exact values available in the table below.`;
    },
  },
  created() {
    this.loadCapabilities();
  },
  beforeDestroy() {
    this.reportSerial += 1;
  },
  methods: {
    async loadCapabilities() {
      this.capabilityState = "loading";
      try {
        const response = await axios.get("/analytics/orders/v2/capabilities");
        this.capabilities = response.data || {};
        if (
          !this.capabilities.api_enabled ||
          !this.capabilities.view_enabled ||
          !(this.capabilities.scopes || []).length
        ) {
          this.capabilityState = "unavailable";
          return;
        }
        if (!this.capabilities.disputes_enabled) {
          this.draft.include_disputes = false;
          this.applied.include_disputes = false;
        }
        this.capabilityState = "ready";
        this.fetchReport();
      } catch (_) {
        this.capabilityState = "error";
      }
    },
    setMode(mode) {
      this.draft.mode = mode;
      this.filtersExpanded = true;
      if (mode === "activity" && !this.draft.from) {
        const date = new Date();
        date.setUTCDate(date.getUTCDate() - 29);
        this.draft.from = date.toISOString().slice(0, 10);
      }
    },
    onTenantChange() {
      this.draft.university_slug = "";
      this.draft.ceremony_ids = [];
    },
    onUniversityChange() {
      this.draft.ceremony_ids = [];
    },
    resetFilters() {
      this.draft = initialFilters();
      if (!this.capabilities.disputes_enabled)
        this.draft.include_disputes = false;
      this.applyFilters();
    },
    applyFilters() {
      if (
        this.invalidDates ||
        (this.draft.mode === "activity" && (!this.draft.from || !this.draft.to))
      )
        return;
      this.applied = {
        ...this.draft,
        ceremony_ids: [...this.draft.ceremony_ids],
        offset: 0,
      };
      this.filtersExpanded = false;
      this.fetchReport();
    },
    params(includePage = true) {
      const f = this.applied;
      const params = {
        mode: f.mode,
        channel: f.channel,
        include_disputes: String(f.include_disputes),
      };
      if (f.tenant) params.tenant = f.tenant;
      if (f.university_slug) params.university_slug = f.university_slug;
      if (f.ceremony_ids.length) params.ceremony_ids = f.ceremony_ids.join(",");
      if (f.mode === "ceremony") params.cutoff = f.cutoff;
      else {
        params.from = f.from;
        params.to = f.to;
      }
      if (includePage) {
        params.limit = 50;
        params.offset = f.offset;
      }
      return params;
    },
    async fetchReport() {
      const serial = ++this.reportSerial;
      this.reportState = "loading";
      this.reportError = "";
      this.exportError = "";
      try {
        const response = await axios.get("/analytics/orders/v2", {
          params: this.params(),
        });
        if (serial !== this.reportSerial) return;
        this.report = response.data;
        this.reportState = "ready";
      } catch (error) {
        if (serial !== this.reportSerial) return;
        this.report = null;
        this.reportState = "error";
        this.reportError =
          error.response && error.response.status === 403
            ? "Your access to this scope has changed. Reset the filters or contact an administrator."
            : "Check your connection and try again.";
      }
    },
    changePage(direction) {
      this.applied = {
        ...this.applied,
        offset: Math.max(0, this.applied.offset + direction * 50),
      };
      this.fetchReport();
      this.$nextTick(() => {
        const title = this.$el.querySelector("#transactions-title");
        if (title) title.scrollIntoView({ block: "start" });
      });
    },
    async exportCsv() {
      if (
        !this.report ||
        this.hasPendingChanges ||
        this.reportState !== "ready"
      )
        return;
      this.exporting = true;
      this.exportError = "";
      try {
        const response = await axios.get("/analytics/orders/v2/export", {
          params: this.params(false),
          responseType: "blob",
        });
        const url = URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `sales-performance-${
          this.applied.mode
        }-${todayUtc()}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } catch (_) {
        this.exportError = "The CSV could not be prepared. Please try again.";
      } finally {
        this.exporting = false;
      }
    },
    formatCount(value) {
      return Number(value || 0).toLocaleString("en-GB");
    },
    orderCountLabel(value) {
      return `${this.formatCount(value)} ${
        Number(value) === 1 ? "order" : "orders"
      }`;
    },
    formatMoney(value, money) {
      if (value === null || value === undefined) return "Amount pending";
      const exponent = Number.isInteger(money.currency_exponent)
        ? money.currency_exponent
        : 2;
      const amount = Number(value) / Math.pow(10, exponent);
      try {
        return new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: money.currency,
          minimumFractionDigits: exponent,
          maximumFractionDigits: exponent,
        }).format(amount);
      } catch (_) {
        return `${money.currency} ${amount.toFixed(exponent)}`;
      }
    },
    negativeMagnitude(value, money) {
      return Number(value || 0) === 0
        ? this.formatMoney(0, money)
        : `−${this.formatMoney(value, money)}`;
    },
    nullableMoney(value, currency, exponent) {
      return value === null || value === undefined || !currency
        ? "Amount pending"
        : this.formatMoney(value, { currency, currency_exponent: exponent });
    },
    signedFee(value, money) {
      return Number(value || 0) === 0
        ? this.formatMoney(0, money)
        : value < 0
        ? `+${this.formatMoney(-value, money)}`
        : `−${this.formatMoney(value, money)}`;
    },
    formatDateTime(value) {
      if (!value) return "Not yet available";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? value
        : new Intl.DateTimeFormat("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC",
            timeZoneName: "short",
          }).format(date);
    },
    channelLabel(channel) {
      return channel === "pos"
        ? "In person"
        : channel === "online"
        ? "Online"
        : "All channels";
    },
    tenantLabel(tenant) {
      return tenant === "mygraduationfilm"
        ? "MyGraduationClip"
        : tenant === "gfpevents"
        ? "GFP Events"
        : tenant;
    },
    productLabel(type) {
      return type === "digital_download"
        ? "Digital download"
        : type === "clip"
        ? "Clip"
        : type || "Product";
    },
    movementLabel(type) {
      const labels = {
        payment: "Payment",
        refund: "Refund",
        processing_fee: "Processing fee",
        dispute_deduction: "Dispute deduction",
        dispute_fee: "Dispute fee",
        dispute_reversal: "Dispute reversal",
      };
      return labels[type] || (type || "Financial movement").replace(/_/g, " ");
    },
    movementAmount(movement) {
      if (
        movement.settlement_amount_minor !== null &&
        movement.settlement_amount_minor !== undefined
      )
        return this.nullableMoney(
          movement.settlement_amount_minor,
          movement.settlement_currency,
          movement.settlement_currency_exponent
        );
      return this.nullableMoney(
        movement.charge_amount_minor,
        movement.charge_currency,
        movement.charge_currency_exponent
      );
    },
    movementAmountBasis(movement) {
      return movement.settlement_amount_minor !== null &&
        movement.settlement_amount_minor !== undefined
        ? "Settlement amount"
        : "Original amount";
    },
    evidenceLabel(state) {
      if (state === "complete") return "Evidence complete";
      if (state === "unknown_amount") return "Amount unknown";
      if (state === "pending") return "Evidence pending";
      return "Evidence incomplete";
    },
    universityName(id) {
      if (!id) return "Not attributed";
      for (const scope of this.capabilities.scopes || []) {
        const university = (scope.universities || []).find(
          (item) => item.id === id
        );
        if (university) return university.name;
      }
      return id;
    },
    ceremonyName(id) {
      if (!id) return "Not attributed";
      for (const scope of this.capabilities.scopes || []) {
        for (const university of scope.universities || []) {
          const ceremony = (university.ceremonies || []).find(
            (item) => item.id === id
          );
          if (ceremony) return this.formatDateTime(ceremony.datetime);
        }
      }
      return id;
    },
    netSummary(row) {
      return (
        (row.settlement_currencies || [])
          .map(
            (money) =>
              `${
                money.net_label || "Processor net proceeds"
              }: ${this.formatMoney(money.net_minor, money)}`
          )
          .join(" · ") || "No settlement amount"
      );
    },
    selectCeremony(row) {
      this.draft.tenant = row.tenant;
      this.draft.university_slug = row.university_slug;
      this.draft.ceremony_ids = [row.ceremony_id];
      this.applyFilters();
      this.$nextTick(() =>
        this.$el
          .querySelector(".applied-bar")
          .scrollIntoView({ block: "start" })
      );
    },
  },
};
</script>

<style scoped>
.sales-page {
  max-width: 1260px;
  margin: 0 auto;
  padding: 8px 0 64px;
  color: #17263d;
}
.page-top,
.section-heading,
.filter-heading,
.applied-bar,
.filter-actions,
.breakdown-row,
.product-row,
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}
.page-top {
  align-items: flex-start;
  margin-bottom: 27px;
}
.eyebrow {
  margin: 0 0 8px;
  color: #356caa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
}
.page-top h1 {
  font-size: clamp(30px, 3vw, 40px);
  line-height: 1.12;
  letter-spacing: -0.035em;
  font-weight: 750;
  margin: 0;
}
.intro {
  margin: 10px 0 0;
  color: #5d6b7f;
  max-width: 610px;
  line-height: 1.5;
}
.previous-link {
  color: #315f96;
  font-size: 13px;
  font-weight: 650;
  white-space: nowrap;
  padding-top: 8px;
}
.previous-link:hover {
  text-decoration: underline;
}
.mode-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  background: #e9eef5;
  padding: 5px;
  border-radius: 14px;
  max-width: 630px;
  margin-bottom: 22px;
}
.mode-switch button {
  border: 0;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  padding: 12px 18px;
  cursor: pointer;
  color: #52647a;
}
.mode-switch button.active {
  background: #fff;
  color: #173f77;
  box-shadow: 0 1px 5px #163c6519;
}
.mode-title,
.mode-description {
  display: block;
}
.mode-title {
  font-size: 14px;
  font-weight: 750;
}
.mode-description {
  font-size: 12px;
  margin-top: 2px;
}
.filter-panel,
.money-panel,
.detail-card,
.transactions-section {
  background: #fff;
  border: 1px solid #dce4ed;
  border-radius: 14px;
  box-shadow: 0 3px 15px #19385808;
}
.filter-panel {
  padding: 22px 24px;
}
.filter-heading {
  align-items: flex-start;
  margin-bottom: 20px;
}
.filter-heading h2,
.section-heading h2 {
  margin: 0;
  font-size: 20px;
  letter-spacing: -0.02em;
}
.filter-heading p,
.section-heading p:not(.eyebrow) {
  color: #65758b;
  font-size: 13px;
  margin: 5px 0 0;
}
.timezone,
.basis-pill {
  white-space: nowrap;
  color: #4b6380;
  background: #edf3f9;
  border-radius: 100px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 700;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}
.filter-grid label {
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 12px;
  font-weight: 700;
  color: #42556d;
}
.filter-grid select,
.filter-grid input {
  width: 100%;
  min-height: 41px;
  padding: 8px 10px;
  border: 1px solid #cbd6e3;
  border-radius: 8px;
  background: #fff;
  color: #182c48;
  font-size: 13px;
}
.ceremony-picker {
  margin-top: 18px;
  border-top: 1px solid #e9edf2;
  padding-top: 16px;
  font-size: 13px;
}
.ceremony-picker summary {
  cursor: pointer;
  font-weight: 700;
  color: #2d5d96;
}
.ceremony-picker summary span {
  color: #78889b;
  font-weight: 500;
  margin-left: 5px;
}
.ceremony-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
  margin-top: 13px;
  max-height: 175px;
  overflow: auto;
}
.ceremony-list label {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  padding: 8px;
  border: 1px solid #e7edf3;
  border-radius: 7px;
}
.ceremony-list input {
  margin-top: 3px;
}
.dispute-toggle {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding-top: 18px;
  margin-top: 16px;
  border-top: 1px solid #e9edf2;
  cursor: pointer;
}
.dispute-toggle input {
  margin-top: 3px;
  accent-color: #1c5aa0;
}
.dispute-toggle strong,
.dispute-toggle small {
  display: block;
}
.dispute-toggle strong {
  font-size: 13px;
}
.dispute-toggle small {
  color: #687b91;
  margin-top: 2px;
  font-size: 12px;
}
.filter-actions {
  justify-content: flex-start;
  margin-top: 20px;
}
.primary-button,
.secondary-button,
.export-button {
  border-radius: 8px;
  min-height: 39px;
  padding: 8px 15px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.primary-button {
  border: 1px solid #1a5c9f;
  color: white;
  background: #1e63a8;
}
.primary-button:hover {
  background: #154e89;
}
.secondary-button,
.export-button {
  border: 1px solid #bdcbd9;
  color: #224e82;
  background: #fff;
}
.secondary-button:hover,
.export-button:hover {
  background: #eef5fc;
}
.text-button {
  border: 0;
  background: none;
  color: #356697;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.validation {
  font-size: 12px;
  color: #a23434;
}
.applied-bar {
  min-height: 65px;
  flex-wrap: wrap;
  padding: 14px 1px;
  font-size: 12px;
  color: #65758b;
}
.applied-bar strong {
  color: #253d59;
  font-size: 13px;
  font-weight: 600;
  margin-right: auto;
}
.applied-label {
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 10px;
}
.pending-note {
  color: #a45f14;
  font-weight: 700;
}
.export-button {
  white-space: nowrap;
}
.state-panel,
.notice {
  padding: 22px;
  border-radius: 12px;
  background: #f1f6fb;
  color: #375371;
  margin: 10px 0 22px;
}
.state-panel h2 {
  font-size: 18px;
  margin: 0 0 5px;
}
.state-panel p {
  margin: 0 0 12px;
}
.state-error {
  background: #fff5f3;
  color: #913d32;
}
.notice {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  font-size: 13px;
  margin: 0 0 12px;
}
.notice-warning {
  color: #795015;
  background: #fff5dc;
  border: 1px solid #f4dfa9;
}
.freshness {
  font-size: 12px;
  color: #6b7c8f;
  margin: 3px 0 25px;
}
.coverage-detail {
  color: #5e6875;
  font-size: 12px;
  background: #f7f9fc;
  border: 1px solid #e1e7ef;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
}
.coverage-detail ul {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 24px;
  margin: 3px 0 0;
  padding-left: 18px;
}
.overview-section,
.details-section {
  margin: 30px 0 38px;
}
.section-heading {
  margin-bottom: 16px;
  align-items: flex-end;
}
.count-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 13px;
}
.count-card {
  border: 1px solid #dce4ed;
  border-radius: 12px;
  background: #fff;
  padding: 20px;
}
.count-card span,
.count-card small,
.count-card strong {
  display: block;
}
.count-card span {
  font-size: 12px;
  color: #58708a;
  font-weight: 700;
}
.count-card strong {
  font-size: 30px;
  line-height: 1.15;
  margin: 8px 0 4px;
  letter-spacing: -0.04em;
}
.count-card small {
  font-size: 11px;
  color: #7a8899;
}
.money-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 13px;
  margin-top: 13px;
}
.money-panel {
  padding: 22px;
}
.money-heading {
  padding-bottom: 16px;
  border-bottom: 1px solid #e7edf3;
}
.money-heading h3 {
  font-size: 16px;
  margin: 0;
}
.money-heading p {
  margin: 4px 0 0;
  color: #748397;
  font-size: 12px;
}
.currency-block {
  border-bottom: 1px solid #edf1f5;
  padding: 15px 0 4px;
}
.currency-block:last-child {
  border-bottom: 0;
}
.currency-title {
  font-weight: 800;
  color: #2c6198;
  font-size: 12px;
  letter-spacing: 0.08em;
}
.currency-block dl {
  margin: 9px 0 0;
}
.currency-block dl div {
  display: flex;
  justify-content: space-between;
  gap: 15px;
  padding: 5px 0;
  font-size: 13px;
}
.currency-block dt {
  color: #66788b;
}
.currency-block dd {
  margin: 0;
  font-weight: 650;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.currency-block .net-row {
  border-top: 1px solid #dce6ef;
  margin-top: 7px;
  padding: 13px 0 10px;
  font-size: 15px;
}
.net-row dt,
.net-row dd {
  color: #143d70;
  font-weight: 800;
}
.incomplete-tag {
  font-size: 10px;
  letter-spacing: 0;
  color: #8b5c19;
  background: #fff0ce;
  border-radius: 4px;
  padding: 3px 5px;
}
.empty-inline {
  color: #60758c;
  background: #f5f8fb;
  border: 1px dashed #cfdbe8;
  border-radius: 10px;
  padding: 21px;
  margin: 12px 0;
  font-size: 13px;
}
.muted {
  color: #79899a;
  font-size: 13px;
}
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
}
.detail-card {
  padding: 20px 22px;
}
.detail-card h3 {
  margin: 0 0 12px;
  font-size: 15px;
}
.detail-card .subheading {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #edf1f5;
}
.breakdown-row,
.product-row {
  border-top: 1px solid #edf1f5;
  padding: 12px 0;
  font-size: 13px;
}
.breakdown-row strong,
.product-row strong {
  display: block;
  font-weight: 650;
}
.breakdown-row small,
.product-row small {
  display: block;
  font-size: 11px;
  color: #7d8b9a;
  margin-top: 3px;
}
.breakdown-row > span {
  color: #315f91;
  font-weight: 700;
  white-space: nowrap;
}
.product-money {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.product-money span {
  display: block;
  color: #315f91;
  font-weight: 700;
}
.transactions-section {
  padding: 23px;
}
.transaction-count {
  font-size: 12px;
  font-weight: 700;
  color: #667c95;
  white-space: nowrap;
}
.transaction {
  border-top: 1px solid #e6edf4;
}
.transaction summary {
  display: flex;
  align-items: center;
  gap: 18px;
  min-height: 65px;
  cursor: pointer;
  list-style: none;
  padding: 10px 3px;
}
.transaction summary::-webkit-details-marker {
  display: none;
}
.transaction-main {
  flex: 1;
  min-width: 0;
}
.transaction-main strong,
.transaction-main small,
.transaction-amount small {
  display: block;
}
.transaction-main strong {
  font-size: 13px;
}
.transaction-main small,
.transaction-amount small {
  color: #7c8b9c;
  font-size: 11px;
  margin-top: 3px;
}
.transaction-amount {
  text-align: right;
  font-size: 14px;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
}
.transaction-amount small {
  font-weight: 500;
}
.chevron {
  color: #6e85a0;
  transition: transform 0.15s;
}
.transaction[open] .chevron {
  transform: rotate(180deg);
}
.transaction-detail {
  background: #f6f9fc;
  border-radius: 8px;
  padding: 17px;
  margin-bottom: 12px;
}
.transaction-detail dl {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px 25px;
  margin: 0;
}
.transaction-detail dt {
  font-size: 11px;
  color: #718197;
}
.transaction-detail dd {
  margin: 2px 0 0;
  font-size: 12px;
  overflow-wrap: anywhere;
}
.transaction-items {
  border-top: 1px solid #dce5ed;
  padding-top: 12px;
  margin-top: 14px;
  font-size: 12px;
}
.transaction-items p {
  margin: 6px 0 0;
}
.pagination {
  justify-content: flex-end;
  margin-top: 17px;
  font-size: 12px;
  color: #718197;
}
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
button:focus-visible,
a:focus-visible,
summary:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 3px solid #66a3e1;
  outline-offset: 2px;
}
.trend-card {
  margin-top: 13px;
  padding: 20px 22px 14px;
  background: #fff;
  border: 1px solid #dce4ed;
  border-radius: 12px;
}
.trend-card h3 {
  margin: 0;
  font-size: 15px;
}
.trend-card p {
  margin: 3px 0 15px;
  font-size: 12px;
  color: #748397;
}
.trend-bars {
  display: flex;
  align-items: flex-end;
  height: 92px;
  gap: 2px;
  border-bottom: 1px solid #bfd0e1;
  background: repeating-linear-gradient(
    to top,
    #fff 0,
    #fff 28px,
    #edf2f7 29px,
    #fff 30px
  );
}
.trend-column {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1;
  height: 100%;
  min-width: 1px;
}
.trend-bar {
  width: min(100%, 18px);
  background: #4b87c4;
  border-radius: 3px 3px 0 0;
}
.trend-axis {
  display: flex;
  justify-content: space-between;
  color: #7e8da0;
  font-size: 10px;
  margin-top: 5px;
}
.trend-data {
  margin-top: 9px;
  font-size: 11px;
  color: #48698b;
}
.trend-data summary {
  cursor: pointer;
}
.trend-data table {
  border-collapse: collapse;
  margin: 8px 0;
  width: 180px;
}
.trend-data td,
.trend-data th {
  padding: 4px 8px 4px 0;
  text-align: left;
  border-bottom: 1px solid #e7edf3;
}
.breakdown {
  border-top: 1px solid #edf1f5;
}
.breakdown summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  list-style: none;
  cursor: pointer;
  font-size: 13px;
}
.breakdown summary::-webkit-details-marker {
  display: none;
}
.breakdown summary strong,
.breakdown summary small {
  display: block;
}
.breakdown summary small {
  color: #7d8b9a;
  font-size: 11px;
  margin-top: 3px;
}
.breakdown-headline {
  text-align: right;
  font-weight: 700;
  color: #315f91;
}
.breakdown-money {
  background: #f6f9fc;
  border-radius: 8px;
  padding: 11px 13px;
  margin: 0 0 10px;
}
.breakdown-money > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-bottom: 1px solid #e5edf4;
  padding: 6px 0;
  color: #5c6f83;
  font-size: 11px;
}
.breakdown-money > div:last-of-type {
  border: 0;
}
.breakdown-money strong {
  color: #263f5a;
}
.drill-button {
  padding: 8px 0 2px;
}
.filter-panel.collapsed {
  padding: 15px 20px;
}
.filter-panel.collapsed .filter-heading {
  margin: 0;
  align-items: center;
}
.filter-panel.collapsed .filter-heading p,
.filter-panel.collapsed .filter-grid,
.filter-panel.collapsed .ceremony-picker,
.filter-panel.collapsed .dispute-toggle,
.filter-panel.collapsed .filter-actions {
  display: none;
}
.filter-panel.collapsed .filter-heading h2 {
  font-size: 15px;
}
.filter-panel.collapsed .filter-heading .secondary-button {
  margin-left: auto;
}
@media (max-width: 800px) {
  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .money-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 560px) {
  .sales-page {
    padding: 4px 0 45px;
  }
  .page-top {
    gap: 9px;
    flex-direction: column;
  }
  .previous-link {
    padding-top: 0;
  }
  .intro {
    font-size: 13px;
  }
  .mode-switch {
    max-width: none;
  }
  .mode-switch button {
    padding: 11px;
  }
  .mode-title {
    font-size: 12px;
  }
  .mode-description {
    font-size: 10px;
  }
  .filter-panel {
    padding: 17px;
  }
  .filter-heading {
    flex-wrap: wrap;
  }
  .filter-grid {
    grid-template-columns: 1fr;
  }
  .ceremony-list {
    grid-template-columns: 1fr;
  }
  .filter-actions {
    flex-wrap: wrap;
  }
  .applied-bar {
    align-items: flex-start;
    gap: 7px;
  }
  .applied-bar strong {
    flex-basis: 100%;
    order: 1;
  }
  .applied-bar .pending-note {
    order: 2;
  }
  .applied-bar .export-button {
    order: 3;
    margin-left: auto;
  }
  .count-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 7px;
  }
  .count-card {
    padding: 13px 10px;
  }
  .count-card strong {
    font-size: 25px;
  }
  .count-card span {
    font-size: 11px;
  }
  .count-card small {
    font-size: 10px;
  }
  .money-panel,
  .detail-card,
  .transactions-section {
    padding: 17px;
  }
  .section-heading {
    align-items: flex-start;
  }
  .transaction-detail dl {
    grid-template-columns: 1fr;
  }
  .transaction-amount {
    font-size: 12px;
  }
  .transaction-main small {
    font-size: 10px;
  }
  .pagination {
    justify-content: space-between;
    gap: 5px;
  }
  .pagination button {
    padding: 6px 8px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .chevron {
    transition: none;
  }
}
</style>
